/**
 * @file BoardCanvas.tsx
 * @description Полотно доски на широком экране: колонки стадий с
 * перетаскиванием карточек (phase10.5; phase19.31 — затенение края).
 *
 * Колонки не помещаются — полотно прокручивается вбок, и край, за которым
 * есть ещё колонки, гаснет (`useScrollFade`): видно, что доска продолжается
 * (разбор A: последняя колонка срезалась краем без подсказки). Пустое место
 * полотна тянется мышью (`useDragToScroll`).
 *
 * Компонент монтируется, когда воронка уже загружена: замер ширины для
 * затенения делается на готовом полотне.
 */

import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useCallback } from "react";

import { BoardColumn } from "./BoardColumn";
import { OrderKanbanCard } from "../OrderKanbanCard";
import { ReturnKanbanCard } from "../ReturnKanbanCard";
import { useScrollFade } from "../../ui/useScrollFade";
import { useDragToScroll } from "../../../hooks/useDragToScroll";
import type { OrdersBoardState } from "../../../hooks/useOrdersBoard";
import type { OrderDto } from "../../../lib/api-orders";
import type { ReturnDto } from "../../../lib/api-returns";

interface Props {
  board: OrdersBoardState;
  onOrderClick: (order: OrderDto) => void;
  onReturnClick: (ret: ReturnDto) => void;
}

export function BoardCanvas({ board, onOrderClick, onReturnClick }: Props) {
  const dragRef = useDragToScroll();
  const fade = useScrollFade<HTMLDivElement>();
  const fadeRef = fade.ref;
  // Один узел — два хука: прокрутка мышью и затенение края.
  const canvasRef = useCallback(
    (el: HTMLDivElement | null) => {
      fadeRef.current = el;
      dragRef(el);
    },
    [dragRef, fadeRef],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={board.onDragStart} onDragEnd={board.onDragEnd}>
      <div
        ref={canvasRef}
        onScroll={fade.update}
        style={fade.style}
        className="min-h-0 min-w-0 flex-1 cursor-grab overflow-x-auto pb-2"
      >
        <div className="flex h-full gap-3">
          {board.stages.map((stage) => (
            <BoardColumn
              key={stage.id}
              boardType={board.boardType}
              stage={stage}
              orders={board.boardType === "orders" ? board.orders.filter((o) => o.stage?.id === stage.id) : []}
              returns={board.boardType === "returns" ? board.returns.filter((r) => r.stage?.id === stage.id) : []}
              onOrderClick={onOrderClick}
              onReturnClick={onReturnClick}
            />
          ))}
        </div>
      </div>
      <DragOverlay>
        {board.activeOrder ? (
          <OrderKanbanCard order={board.activeOrder} dragging />
        ) : board.activeReturn ? (
          <ReturnKanbanCard ret={board.activeReturn} dragging />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
